import React, { useEffect, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Input, TextArea, Button } from "../../components/common/Primitives";
import { Plus, Save, Trash2, Eye, Code, FileText, History, RotateCcw, X as XIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";

const BLANK = { slug: "", title: "", seo_description: "", content: "", active: true };

export default function AdminCMS() {
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [isNew, setIsNew] = useState(false);
  const [view, setView] = useState("edit"); // "edit" | "preview" | "split"
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [previewRev, setPreviewRev] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cms");
      setPages(data || []);
      if (!selectedSlug && data?.[0]) selectSlug(data[0].slug, data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [selectedSlug]);
  useEffect(() => { load(); }, []);

  const selectSlug = (slug, list) => {
    const src = list || pages;
    const doc = src.find((p) => p.slug === slug);
    if (!doc) return;
    setSelectedSlug(slug);
    setForm({
      slug: doc.slug,
      title: doc.title || "",
      seo_description: doc.seo_description || "",
      content: doc.content || "",
      active: doc.active !== false,
    });
    setIsNew(false);
    setDirty(false);
  };

  const startNew = () => {
    setSelectedSlug(null);
    setForm(BLANK);
    setIsNew(true);
    setDirty(true);
    setView("edit");
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required.");
    if (isNew && !form.slug.trim()) return toast.error("Slug required for new pages.");
    if (isNew && !/^[a-z0-9-]+$/.test(form.slug)) return toast.error("Slug must be lowercase letters, numbers, and hyphens only.");
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/cms", form);
        toast.success(`Page /${form.slug} created.`);
      } else {
        const { slug, ...body } = form;
        await api.put(`/admin/cms/${slug}`, body);
        toast.success(`Page /${slug} saved.`);
      }
      const { data } = await api.get("/cms");
      setPages(data || []);
      setIsNew(false);
      setDirty(false);
      setSelectedSlug(form.slug);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!selectedSlug) return;
    if (!window.confirm(`Delete /${selectedSlug}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/cms/${selectedSlug}`);
      toast.success("Deleted.");
      setSelectedSlug(null);
      setForm(BLANK);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openHistory = async () => {
    if (!selectedSlug) return;
    try {
      const { data } = await api.get(`/admin/cms/${selectedSlug}/revisions`);
      setRevisions(data || []);
      setPreviewRev(null);
      setShowHistory(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revertTo = async (rev) => {
    if (!selectedSlug || !rev) return;
    if (!window.confirm(`Revert /${selectedSlug} to revision #${rev.revision_number}? Your current version will be snapshotted first.`)) return;
    try {
      const { data } = await api.post(`/admin/cms/${selectedSlug}/revert/${rev.revision_id}`);
      toast.success(`Reverted to #${rev.revision_number}.`);
      setForm({
        slug: data.slug,
        title: data.title || "",
        seo_description: data.seo_description || "",
        content: data.content || "",
        active: data.active !== false,
      });
      setDirty(false);
      openHistory(); // refresh history list (revert creates a new snapshot)
      const listResp = await api.get("/cms");
      setPages(listResp.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const insertMd = (before, after = before) => {
    const ta = document.querySelector("[data-testid=cms-content-editor]");
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const sel = form.content.slice(start, end);
    const newContent = form.content.slice(0, start) + before + sel + after + form.content.slice(end);
    setForm((f) => ({ ...f, content: newContent }));
    setDirty(true);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + before.length, end + before.length); }, 0);
  };

  const patch = (updates) => { setForm((f) => ({ ...f, ...updates })); setDirty(true); };

  const editor = (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1 pb-2 border-b border-navy/10" data-testid="cms-toolbar">
        <ToolBtn onClick={() => insertMd("**")} label="Bold" testId="fmt-bold" />
        <ToolBtn onClick={() => insertMd("*")} label="Italic" testId="fmt-italic" />
        <ToolBtn onClick={() => insertMd("## ", "")} label="H2" testId="fmt-h2" />
        <ToolBtn onClick={() => insertMd("### ", "")} label="H3" testId="fmt-h3" />
        <ToolBtn onClick={() => insertMd("- ", "")} label="List" testId="fmt-list" />
        <ToolBtn onClick={() => insertMd("1. ", "")} label="Numbered" testId="fmt-numbered" />
        <ToolBtn onClick={() => insertMd("> ", "")} label="Quote" testId="fmt-quote" />
        <ToolBtn onClick={() => insertMd("`")} label="Code" testId="fmt-code" />
        <ToolBtn onClick={() => insertMd("[", "](https://)")} label="Link" testId="fmt-link" />
        <ToolBtn onClick={() => insertMd("\n---\n", "")} label="Divider" testId="fmt-divider" />
      </div>
      <textarea
        data-testid="cms-content-editor"
        value={form.content}
        onChange={(e) => patch({ content: e.target.value })}
        placeholder="# Heading

Write in **Markdown**. Use the toolbar above or type freely.

- Bullet lists
- Bold, italic, and [links](https://example.com)"
        className="w-full h-[520px] font-mono text-sm bg-white border border-navy/15 text-ink px-4 py-3 rounded-md focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition-all"
      />
    </div>
  );

  const preview = (
    <div
      className="glass rounded-md p-6 h-[560px] overflow-y-auto cms-preview"
      data-testid="cms-preview"
    >
      {form.content?.trim() ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content}</ReactMarkdown>
      ) : (
        <div className="text-ink-faint text-sm">Nothing to preview yet.</div>
      )}
    </div>
  );

  return (
    <DashboardShell
      title="CMS"
      subtitle="ADMIN · CONTENT"
      actions={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={startNew} data-testid="cms-new-page"><Plus size={14} className="inline mr-1" /> New Page</Button>
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Sidebar list */}
        <Card testId="cms-page-list" className="lg:col-span-1">
          <div className="overline mb-3 flex items-center justify-between">
            <span>Pages · {pages.length}</span>
          </div>
          <div className="space-y-1 max-h-[560px] overflow-y-auto">
            {pages.map((p) => (
              <button
                key={p.slug}
                onClick={() => selectSlug(p.slug)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedSlug === p.slug ? "bg-gold/10 border border-gold/50" : "hover:bg-navy/[0.03] border border-transparent"}`}
                data-testid={`cms-nav-${p.slug}`}
              >
                <div className="font-heading text-ink truncate">{p.title}</div>
                <div className="text-[10px] font-mono text-ink-muted flex items-center justify-between mt-0.5">
                  <span>/{p.slug}</span>
                  <Pill tone={p.active ? "success" : "muted"}>{p.active ? "LIVE" : "DRAFT"}</Pill>
                </div>
              </button>
            ))}
            {pages.length === 0 && <div className="text-ink-faint text-sm">No pages yet.</div>}
          </div>
        </Card>

        {/* Editor pane */}
        <Card className="lg:col-span-3" testId="cms-editor-pane">
          {!selectedSlug && !isNew ? (
            <div className="text-center py-16 text-ink-muted text-sm">
              <FileText size={32} className="mx-auto mb-3 text-ink-faint" />
              Select a page to edit or create a new one.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="Title"
                  value={form.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  testId="cms-title-input"
                />
                <Input
                  label="Slug"
                  value={form.slug}
                  onChange={(e) => patch({ slug: e.target.value })}
                  disabled={!isNew}
                  testId="cms-slug-input"
                  placeholder="e.g. faq"
                />
                <Input
                  label="SEO Description"
                  value={form.seo_description}
                  onChange={(e) => patch({ seo_description: e.target.value })}
                  testId="cms-seo-input"
                />
                <label className="flex items-end gap-2 mt-2" data-testid="cms-active-toggle">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => patch({ active: e.target.checked })}
                    className="w-4 h-4 accent-gold"
                  />
                  <span className="text-sm text-ink-soft">Publish (visible on public site)</span>
                </label>
              </div>

              {/* View mode toggle */}
              <div className="flex items-center justify-between border-b border-navy/10 pb-3">
                <div className="inline-flex gap-1 glass rounded-full p-1">
                  <ViewTab active={view === "edit"} onClick={() => setView("edit")} icon={Code} label="Edit" testId="cms-view-edit" />
                  <ViewTab active={view === "split"} onClick={() => setView("split")} icon={FileText} label="Split" testId="cms-view-split" />
                  <ViewTab active={view === "preview"} onClick={() => setView("preview")} icon={Eye} label="Preview" testId="cms-view-preview" />
                </div>
                <div className="flex gap-2">
                  {!isNew && (
                    <Button variant="ghost" onClick={() => openHistory()} data-testid="cms-history-btn">
                      <History size={14} className="inline mr-1" /> History
                    </Button>
                  )}
                  {!isNew && <Button variant="danger" onClick={remove} data-testid="cms-delete-btn"><Trash2 size={14} className="inline mr-1" /> Delete</Button>}
                  <Button onClick={save} disabled={!dirty || saving} data-testid="cms-save-btn"><Save size={14} className="inline mr-1" /> {saving ? "Saving…" : (isNew ? "Create Page" : "Save")}</Button>
                </div>
              </div>

              {view === "edit" && editor}
              {view === "preview" && preview}
              {view === "split" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {editor}
                  {preview}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Revision History Drawer */}
      {showHistory && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-stretch justify-end" onClick={() => setShowHistory(false)} data-testid="cms-history-drawer">
          <div className="bg-white h-full w-full max-w-2xl shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-navy/10 p-5 flex justify-between items-center z-10">
              <div>
                <div className="font-heading text-ink text-lg">Revision History</div>
                <div className="text-xs font-mono text-ink-muted">/{selectedSlug} · {revisions.length} snapshots</div>
              </div>
              <button onClick={() => setShowHistory(false)} className="text-ink-muted hover:text-ink" data-testid="cms-history-close"><XIcon size={20} /></button>
            </div>
            <div className="p-5 space-y-3">
              {revisions.length === 0 && <div className="text-ink-muted text-sm">No revisions yet. Save the page to create your first snapshot.</div>}
              {revisions.map((r) => (
                <div key={r.revision_id} className="border border-navy/10 rounded-md p-4 hover:border-gold/50 transition-all" data-testid={`cms-rev-${r.revision_number}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Pill tone="gold">#{r.revision_number}</Pill>
                        <span className="font-heading text-ink text-sm">{r.title}</span>
                        {!r.active && <Pill tone="muted">DRAFT</Pill>}
                      </div>
                      <div className="text-[11px] font-mono text-ink-muted">
                        {formatDateTime(r.saved_at)} · by {r.saved_by}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => setPreviewRev(previewRev?.revision_id === r.revision_id ? null : r)} className="px-2 py-1 text-[10px] font-heading text-ink-soft hover:bg-navy/[0.03] rounded" data-testid={`cms-rev-preview-${r.revision_number}`}>
                        {previewRev?.revision_id === r.revision_id ? "HIDE" : "PREVIEW"}
                      </button>
                      <button onClick={() => revertTo(r)} className="px-2 py-1 text-[10px] font-heading text-[#8A6600] hover:bg-gold/10 rounded flex items-center gap-1" data-testid={`cms-rev-revert-${r.revision_number}`}>
                        <RotateCcw size={11} /> REVERT
                      </button>
                    </div>
                  </div>
                  {previewRev?.revision_id === r.revision_id && (
                    <div className="mt-4 border-t border-navy/10 pt-4">
                      <div className="cms-preview text-sm max-h-72 overflow-y-auto p-4 bg-navy/[0.02] rounded" data-testid={`cms-rev-body-${r.revision_number}`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{r.content || "*(empty)*"}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function ToolBtn({ onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="px-2.5 py-1 text-xs font-mono text-ink-soft hover:bg-gold/10 hover:text-[#8A6600] rounded transition-colors"
    >
      {label}
    </button>
  );
}

function ViewTab({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-heading font-semibold rounded-full flex items-center gap-1 transition-all ${active ? "bg-navy text-white shadow-[0_2px_8px_rgba(15,61,46,0.15)]" : "text-ink-soft hover:text-ink"}`}
    >
      <Icon size={12} /> {label}
    </button>
  );
}
